/**
 * Kiosk overlay for a menu item's required single-pick options
 * ("Which sandwich?"). Add to Order stays disabled until exactly one
 * choice is tapped, so an order the backend would reject can never be
 * built from the kiosk.
 */

import { useState } from 'react';
import { KioskOverlay } from './KioskOverlay';
import type { ItemOptionsPayload } from '../../hooks/useMenu';

interface ItemOptionsOverlayProps {
  itemName: string;
  itemOptions: ItemOptionsPayload;
  initialSelected?: number[];
  confirmLabel?: string; // "Add to Order" | "Update Order"
  onAdd: (optionIds: number[], optionNames: string[]) => void;
  onCancel: () => void;
}

export function ItemOptionsOverlay({
  itemName,
  itemOptions,
  initialSelected,
  confirmLabel = 'Add to Order',
  onAdd,
  onCancel,
}: ItemOptionsOverlayProps) {
  const [selected, setSelected] = useState<number[]>(initialSelected ?? []);
  const hasPick = selected.length === 1;

  const commit = () => {
    if (!hasPick) return;
    const names = itemOptions.options.filter((o) => selected.includes(o.id)).map((o) => o.name);
    onAdd(selected, names);
  };

  return (
    <KioskOverlay
      title={itemName}
      onDismiss={onCancel}
      footer={
        <div className="flex gap-4">
          <button
            onClick={commit}
            disabled={!hasPick}
            className={`flex-1 h-20 rounded-2xl text-2xl font-semibold transition-all
                       ${hasPick
                         ? 'bg-[#10348e] text-white hover:bg-[#0d2b73] active:scale-[0.98] active:bg-[#0a2260]'
                         : 'bg-gray-200 text-gray-400 cursor-not-allowed'}`}
          >
            {hasPick ? confirmLabel : 'Choose one above'}
          </button>
          <button
            onClick={onCancel}
            className="h-20 px-10 bg-gray-100 hover:bg-gray-200 active:bg-gray-300
                       text-gray-600 text-xl font-medium rounded-2xl transition-all"
          >
            Cancel
          </button>
        </div>
      }
    >
      <h3 className="text-3xl font-light text-gray-900 mb-8">
        {itemOptions.prompt || 'Choose one'}
      </h3>
      <div className="flex flex-wrap gap-4">
        {itemOptions.options.map((option) => {
          const isSelected = selected.includes(option.id);
          return (
            <button
              key={option.id}
              onClick={() => setSelected([option.id])}
              className={`min-h-[96px] px-8 rounded-2xl text-2xl font-medium transition-all
                         active:scale-[0.98] flex items-center gap-3
                         ${isSelected
                           ? 'bg-[#10348e] text-white shadow-lg'
                           : 'bg-white text-gray-900 border-2 border-[#1e62ac] hover:bg-blue-50'}`}
            >
              {isSelected && <span className="text-2xl">✓</span>}
              <span>{option.name}</span>
            </button>
          );
        })}
      </div>
    </KioskOverlay>
  );
}
